import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import { Component } from "react"
import PropTypes from "prop-types"
import Geosuggest from "component-lib/Geosuggest"
import Box from "component-lib/Base/components/Box"
import Radio from "component-lib/Base/components/Radio"
import RadioGroup from "component-lib/Base/components/RadioGroup"
import FormControlLabel from "component-lib/Base/components/Form/FormControl/FormControlLabel"
import { handleEnterPress } from "utils-lib/handleEnterPress"
import { withLocalization } from "hoc-lib/localization"
import { DROP_OFF_RECYCLING, lob } from "constants-lib/common"
import ContentLoader from "react-content-loader"
import { withLDConsumer } from "launchdarkly-react-client-sdk"
import { getWindow } from "utils-lib/getWindow"
import { FacilitySearchResults } from "./FacilitySearchResults/FacilitySearchResults"
import "./FacilitySearch.scss"
import {
  pushDropoffLocationFacilitySearch,
  pushDropoffLocationFacilityTypeToggle,
} from "./Analytics/analytics"

const DROP_OFF_LOCATION_FACILITY_TYPE_TOGGLE =
  "dropoff_location_facility_type_toggle"
const DROP_OFF_LOCATION_PATH = "/us/en/drop-off-locations"
const ssrWindow = getWindow()

export const strings = {
  en_CA: {
    LOADING: `Loading...`,
    ADDRESS_NOT_FOUND: `Address not found.`,
    NO_RECYCLING_FACILITIES_FOUND: `No recycling facilities found. Try a different address.`,
    NO_WASTE_FACILITIES_FOUND: `No waste facilities found. Try a different address.`,
    COULD_NOT_FIND_ADDRESS: `We could not find this address. Please try entering your postal code instead.`,
    DROP_OFF_TYPE: `Drop-off type:`,
    RECYCLING: `Recycling`,
    TRASH_DISPOSAL: `Trash Disposal`,
    TRASH_CA_DISPOSAL: `Garbage Disposal`,
    TYPE_ADDRESS_HERE: `Type address here`,
    EMPTY_ADDRESS: `Please select an address.`,
    ADDRESS_INVALID_BUSINESS: `Sorry, we are unable to find your address, please re-enter your address or call us at 855-864-1221`,
    ADDRESS_INVALID_HOME: `Sorry, we are unable to find your address, please re-enter your address or call us at 855-864-1221`,
    GEOSUGGEST_LABEL: `Facility Search`,
  },
  fr_CA: {
    LOADING: `Chargement...`,
    ADDRESS_NOT_FOUND: `Addresse introuvable.`,
    NO_RECYCLING_FACILITIES_FOUND: `Emplacement de recyclage introuvable. Essayez une autre addresse.`,
    NO_WASTE_FACILITIES_FOUND: `Lieu d'enfouissement de déchets introuvable. Essayez une autre addresse.`,
    COULD_NOT_FIND_ADDRESS: `Nous n'avons pu trouver cette adresse. Essayez plutôt d'entrer votre code postal.`,
    DROP_OFF_TYPE: `Type de lieu de dépôt :`,
    RECYCLING: `Recyclage`,
    TRASH_DISPOSAL: `Élimination de déchets`,
    TYPE_ADDRESS_HERE: `Entrez l'adresse ici`,
    EMPTY_ADDRESS: `Veuillez sélectionner une adresse.`,
    ADDRESS_INVALID_BUSINESS: `Désolé, nous n'arrivons pas à trouver votre adresse, veuillez saisir à nouveau votre adresse ou nous appeler au 855 864-1221`,
    ADDRESS_INVALID_HOME: `Désolé, nous n'arrivons pas à trouver votre adresse, veuillez saisir à nouveau votre adresse ou nous appeler au 855 864-1221`,
    GEOSUGGEST_LABEL: `Rechercher installations`,
  },
}

export const FacilitySearchSkeleton = () => (
  <ContentLoader className="facility-search-skeleton">
    <rect x="0" y="0" rx="5" ry="5" width="100%" height="100%" />
  </ContentLoader>
)

class FacilitySearchUnwrapped extends Component {
  constructor(props) {
    super(props)
    this.state = {
      suggestData: true,
      userAddressEntered: false,
      isFirstLoad: true,
      showList: false,
    }
    this.fileIdentifier = `FacilitySearch.jsx`
    this.noResults = ``
    this.currentLocation = ``
  }

  UNSAFE_componentWillMount() {
    const { isShown } = this.props
    const { customerSelections } = this.props.reduxState
    // if component is not shown, do not do set search text or onSuggestIsSelected
    if (
      isShown &&
      get(customerSelections, `customerAddress.formattedAddress`, ``) !== ``
    ) {
      this.doFirstLoad()
    }
  }

  componentDidMount() {
    const { onSuggestNoResults, setHasGeosuggestion } = this.props.reduxActions

    setHasGeosuggestion(true)
    onSuggestNoResults()
  }

  /* Used to show/hide facilities list for mobile/tab view */

  shouldComponentUpdate(nextProps, nextState) {
    return nextProps !== this.props || nextState !== this.state
  }

  componentDidUpdate(prevProps) {
    const { isShown } = this.props
    const { customerSelections } = this.props.reduxState
    // if not shown, skip
    if (isShown) {
      // if  is shown, and has not done initial first load, set search text and onSuggestIsSelected
      if (this.state.isFirstLoad && this.props.isShown !== prevProps.isShown) {
        this.doFirstLoad()
      }

      const currAddress = get(customerSelections, `customerAddress.label`, ``)
      const prevAddress = get(
        prevProps.reduxState.customerSelections,
        `customerAddress.label`,
        ``,
      )

      if (
        customerSelections &&
        !isEmpty(customerSelections.customerAddress) &&
        currAddress !== prevAddress
      ) {
        this.onSuggestIsSelected(customerSelections.customerAddress)
        this.props.setSearchText(currAddress)
        this.onPushDropoffLocationFacilitySearch(
          customerSelections.customerAddress,
          false,
        )
      }
    }
  }

  setShowList = (value) => {
    this.setState({ showList: value })
  }

  getTrashFacilityType = () =>
    this.props.language === "en_CA"
      ? this.props.strings.TRASH_CA_DISPOSAL
      : this.props.strings.TRASH_DISPOSAL

  onToggleDropoffType = () => {
    const facilityType =
      this.props.dropoffType === DROP_OFF_RECYCLING
        ? this.getTrashFacilityType()
        : this.props.strings.RECYCLING

    pushDropoffLocationFacilityTypeToggle(facilityType, this.props.language)

    this.props.clearSelectedFacilityId()
    this.props.onToggleDropoffType()
  }

  onSelectFacility = (id) => {
    this.props.onFacilityListItemClick(id)
  }

  onSuggestNoResults = (suggest) => {
    this.currentLocation = ``
    const { setHasGeosuggestion } = this.props.reduxActions
    this.noResults = suggest
    this.setState({ suggestData: false })
    if (suggest.length === 0) {
      setHasGeosuggestion(false)
    }
  }

  onGeosuggestChange = (suggest) => {
    this.setState({ suggestData: false, userAddressEntered: true })

    this.props.setSearchText(suggest)
  }

  onPushDropoffLocationFacilitySearch = (suggest, shouldTrigger) => {
    if (
      shouldTrigger &&
      ssrWindow.location.pathname === DROP_OFF_LOCATION_PATH
    ) {
      const facilityType =
        this.props.dropoffType === DROP_OFF_RECYCLING
          ? this.props.strings.RECYCLING
          : this.getTrashFacilityType()

      pushDropoffLocationFacilitySearch({
        suggest,
        facilityType,
        language: this.props.language,
      })
    }
  }

  /* When suggested address is selected. */
  onSuggestIsSelected = async (suggest) => {
    this.props.reduxActions.setCustomerAddress(suggest)

    const { setHasGeosuggestion } = this.props.reduxActions

    const { getMapLocations, updateGeosuggestLocation } = this.props
    const { customerSelections } = this.props.reduxState
    // suggest.formattedAddress becomes empty when user type
    // suggest becomes empty when user type
    this.setState({ suggestData: true, userAddressEntered: false })
    if (
      suggest.formattedAddress === `` ||
      suggest === `` ||
      (!(suggest.country === `Canada` || suggest.country === `CA`) &&
        !(suggest.country === `USA` || suggest.country === `US`)) ||
      !suggest.postalCode
    ) {
      setHasGeosuggestion(false)
      getMapLocations(suggest)
      return
    }
    this.currentLocation = suggest.country

    if (
      suggest?.formattedAddress ===
      customerSelections?.customerAddress?.formattedAddress
    ) {
      this.onPushDropoffLocationFacilitySearch(suggest, true)
    }

    updateGeosuggestLocation(suggest)
    getMapLocations(suggest)
    setHasGeosuggestion(true)
    // dispatch the latest location

    // dispatch to get the map locations
    // getMapLocations({
    //   lat: suggest.location.lat,
    //   lng: suggest.location.lng
    // })
  }

  getInfoMessage(mapLocations) {
    const { strings } = this.props

    const { facilityLocator, map } = this.props.reduxState

    if (
      facilityLocator?.fetching === true &&
      facilityLocator?.searchText !== ``
    ) {
      return strings.LOADING
    }

    // const isWaste = facilityLocator.selectedCustomerAction === `drop_off_waste`
    const isWaste = this.props.dropoffType === `drop_off_waste`
    let infoMessage = ``

    // Only show messages if the user has typed something in the search field
    const searchText = facilityLocator?.searchText
    if (searchText !== ``) {
      // Check whether map locations were found
      // const mapLocationsFound =
      //   _.get(facilityLocator, `mapLocations`, []).filter((loc) => {
      //     if (isWaste) {
      //       return (
      //         loc.properties.facTypeCode === `2` ||
      //         loc.properties.facTypeCode === `4`
      //       )
      //     } else {
      //       return loc.properties.facTypeCode === `3`
      //     }
      //   }).length > 0

      // Check whether user has selected an address
      const isAddressSelected =
        facilityLocator?.selectedGeosuggestion !== null &&
        !this.state.userAddressEntered
      const label =
        facilityLocator?.selectedGeosuggestion === null
          ? ``
          : facilityLocator?.selectedGeosuggestion.label
      const onloadCheck = this.state.suggestData && !isAddressSelected

      // If address in search bar is different from the selected address and
      // no suggestions were found, display info message
      if (isEmpty(mapLocations) || onloadCheck) {
        if (isAddressSelected) {
          if (
            searchText !== label &&
            facilityLocator?.hasLoadedLocations &&
            isEmpty(get(map, `mapLocations`, []))
          ) {
            infoMessage = strings.ADDRESS_NOT_FOUND
          } else {
            infoMessage = isWaste
              ? strings.NO_WASTE_FACILITIES_FOUND
              : strings.NO_RECYCLING_FACILITIES_FOUND
          }
        }
      }
    }
    return infoMessage
  }

  doFirstLoad() {
    const { customerSelections } = this.props.reduxState
    this.setState({ isFirstLoad: false }, () => {
      // don't do anything if customer address is empty on the initial load, this prevents showing an error message on the initial load
      if (!isEmpty(customerSelections.customerAddress)) {
        this.onSuggestIsSelected(customerSelections.customerAddress)
        this.props.setSearchText(customerSelections.customerAddress.label)
      }
    })
  }

  renderResults() {
    const { isAccordionItem, map } = this.props

    const facilitySearchResultsProps = {
      map,
      onSelectFacility: this.onSelectFacility,
      onMouseEnterItem: this.props.onMouseEnterItem,
      onMouseLeaveItem: this.props.onMouseLeaveItem,
      dropoffType: this.props.dropoffType,
      isAccordionItem,
      setListStatus: [this.state.showList, this.setShowList],
    }

    const facilityDetailsProps = {
      map,
      onCloseFacility: this.props.clearSelectedFacilityId,
      style: this.props.detailsBoxStyle,
    }

    return (
      <FacilitySearchResults
        {...facilitySearchResultsProps}
        facilityDetailsProps={facilityDetailsProps}
      />
    )
  }

  renderGeosuggest() {
    const { strings, map, reduxState, reduxActions } = this.props

    const { customerSelections, facilityLocator } = reduxState
    const { onSuggestNoResults } = reduxActions

    const isWaste = this.props.dropoffType === `drop_off_waste`
    const mapLocations = get(map, `mapLocations`, []).filter(
      // loc - the current element being checked
      (loc) => {
        const currentFacTypeCode = get(loc, `properties.facTypeCode`, ``)
        return isWaste
          ? currentFacTypeCode === `2` || currentFacTypeCode === `4`
          : currentFacTypeCode === `3`
      },
    )

    const infoMessage = this.getInfoMessage(mapLocations)
    const errorMessage =
      customerSelections.showErrorMessage &&
      (this.noResults.length > 0 || isEmpty(mapLocations))
        ? strings.COULD_NOT_FIND_ADDRESS
        : ``
    let infoErrorMessage = errorMessage || infoMessage
    if (
      !infoErrorMessage &&
      customerSelections.showSuggestError &&
      facilityLocator?.searchText !== ``
    ) {
      infoErrorMessage = (
        <span className="d-flex">
          {get(customerSelections, `customerLob`, lob.HOME) === lob.BUSINESS
            ? strings.ADDRESS_INVALID_BUSINESS
            : strings.ADDRESS_INVALID_HOME}
        </span>
      )
    }
    this.props.customErrorMessage(infoErrorMessage)

    const geosuggestProps = {
      defaultItem: customerSelections.customerAddress,
      infoMessage: infoErrorMessage,
      onGeosuggestChange: this.onGeosuggestChange,
      onSuggestNoResults: this.onSuggestNoResults,
      onSuggestIsSelected: this.onSuggestIsSelected,
      onUpdateSuggests: onSuggestNoResults,
      className: `text-left flex-grow-1 accordion-location`,
      label: strings.GEOSUGGEST_LABEL,
      // showStreetsOnly: false,
      showStreetsOnly: true,
      style: { maxHeight: `4rem` },
      errorStyle:
        errorMessage?.length > 0 ? `errorInfoMessage errorInfoMessage-loc` : ``,
    }

    return <Geosuggest {...geosuggestProps} />
  }

  render() {
    const { strings, map } = this.props

    const { customerSelections } = this.props.reduxState

    const errorMessage =
      customerSelections.showErrorMessage && this.noResults?.length > 0
        ? strings.COULD_NOT_FIND_ADDRESS
        : ``

    return (
      <>
        <div data-testid="FacilitySearch" className="container">
          <div
            className="GeosuggestWithToggle d-flex flex-column no-wrap"
            style={{ width: `100%` }}
          >
            {this.renderGeosuggest()}
            <Box my="2rem">
              <RadioGroup
                row
                className="switchWrapper"
                analytics={DROP_OFF_LOCATION_FACILITY_TYPE_TOGGLE}
                value={this.props.dropoffType}
                onChange={this.onToggleDropoffType}
              >
                <FormControlLabel
                  tabIndex={0}
                  className={`switchChoice radioButton pr-2 mt-1 ${
                    this.props.dropoffType === DROP_OFF_RECYCLING
                      ? `maax-font-weight-bold`
                      : `maax-font-weight-regular`
                  }`}
                  value={DROP_OFF_RECYCLING}
                  onKeyPress={(e) =>
                    handleEnterPress(e, () => {
                      this.onToggleDropoffType()
                    })
                  }
                  control={<Radio />}
                  label={strings.RECYCLING}
                />
                <FormControlLabel
                  tabIndex={0}
                  className={`switchChoice radioButton pr-2 mt-1 ${
                    this.props.dropoffType === `drop_off_waste`
                      ? `maax-font-weight-bold`
                      : `maax-font-weight-regular`
                  }`}
                  value="drop_off_waste"
                  onKeyPress={(e) =>
                    handleEnterPress(e, () => {
                      this.onToggleDropoffType()
                    })
                  }
                  control={<Radio />}
                  label={
                    this.props.language === "en_CA"
                      ? strings.TRASH_CA_DISPOSAL
                      : strings.TRASH_DISPOSAL
                  }
                />
              </RadioGroup>
            </Box>
          </div>
        </div>
        <div
          className={`FacilitySearch ${
            this.props.hasMapLocations ? `searched` : ``
          } ${
            map.selectedFacilityId !== null || this.state.showList
              ? `listOpen`
              : ``
          }`}
        >
          <Box
            className={`${this.props.hasMapLocations ? `has-loc` : ``} ${
              map.selectedFacilityId !== null ? `selected-loc` : ``
            }`}
            style={{
              width: `100%`,
              ...this.props.searchBoxStyle,
            }}
          >
            {(this.currentLocation === `Canada` ||
              this.currentLocation === `CA` ||
              this.currentLocation === `USA` ||
              this.currentLocation === `US`) &&
              this.currentLocation.length > 0 &&
              errorMessage.length === 0 &&
              this.renderResults()}
          </Box>
        </div>
      </>
    )
  }
}

FacilitySearchUnwrapped.propTypes = {
  strings: PropTypes.object,
  dropoffChild: PropTypes.node,
  clearSelectedFacilityId: PropTypes.func,
  map: PropTypes.object,
  onFacilityListItemClick: PropTypes.func,
  setCustomerAction: PropTypes.func,
  setHasGeosuggestion: PropTypes.func,
  setSearchText: PropTypes.func,
  getMapLocations: PropTypes.func,
  updateGeosuggestLocation: PropTypes.func,
  onMouseEnterItem: PropTypes.func,
  onMouseLeaveItem: PropTypes.func,
  isAccordionItem: PropTypes.bool,
  isShown: PropTypes.bool,
  searchBoxStyle: PropTypes.object,
  detailsBoxStyle: PropTypes.object,
  onToggleDropoffType: PropTypes.func.isRequired,
  customErrorMessage: PropTypes.func,
  dropoffType: PropTypes.string.isRequired,
  hasMapLocations: PropTypes.bool.isRequired,
  reduxActions: PropTypes.object.isRequired,
  reduxState: PropTypes.object.isRequired,
  language: PropTypes.string,
}

FacilitySearchUnwrapped.defaultProps = {
  strings: {},
  dropoffChild: "",
  clearSelectedFacilityId: () => {},
  map: {},
  onFacilityListItemClick: () => {},
  setCustomerAction: () => {},
  setHasGeosuggestion: () => {},
  setSearchText: () => {},
  getMapLocations: () => {},
  updateGeosuggestLocation: () => {},
  onMouseEnterItem: () => {},
  onMouseLeaveItem: () => {},
  searchBoxStyle: {},
  detailsBoxStyle: {},
  isAccordionItem: false,
  isShown: true,
  customErrorMessage: () => {},
  language: "",
}

export const FacilitySearch = withLDConsumer()(
  withLocalization(strings)(FacilitySearchUnwrapped),
)
